import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useScenario, useEntityFilter } from '../components/layout/Layout';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, LinearProgress, Autocomplete, IconButton, Grid, CircularProgress } from '@mui/material';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import RemoveIcon from '@mui/icons-material/Remove';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchableDataTable from '../components/SearchableDataTable';
import api from '../services/api';
import '../styles/EntityInputs.css';

const ENTITY_INPUT_COLUMNS = [
{ field: 'ScenarioCode', headerName: 'Scenario Code', minWidth: 120 },
{ field: 'EntityCode', headerName: 'Entity Code', minWidth: 120, flex: 1 },
{
  field: 'CurrentTaxRate',
  headerName: 'Current Tax Rate',
  minWidth: 140,
  valueFormatter: (params) => {
    if (params.value == null || params.value === '') return '';
    const rate = parseFloat(params.value);
    // If value is already a decimal (e.g., 0.21), multiply by 100
    const percentage = rate < 1 ? rate * 100 : rate;
    return `${percentage.toFixed(2)}%`;
  }
},
{
  field: 'PriorCurrentTaxRate',
  headerName: 'Prior Current Tax Rate',
  minWidth: 170,
  valueFormatter: (params) => {
    if (params.value == null || params.value === '') return '';
    const rate = parseFloat(params.value);
    const percentage = rate < 1 ? rate * 100 : rate;
    return `${percentage.toFixed(2)}%`;
  }
},
{
  field: 'BeginningDeferredTaxRate',
  headerName: 'Begin Deferred Tax Rate',
  minWidth: 170,
  valueFormatter: (params) => {
    if (params.value == null || params.value === '') return '';
    const rate = parseFloat(params.value);
    const percentage = rate < 1 ? rate * 100 : rate;
    return `${percentage.toFixed(2)}%`;
  }
},
{
  field: 'EndingDeferredTaxRate',
  headerName: 'End Deferred Tax Rate',
  minWidth: 170,
  valueFormatter: (params) => {
    if (params.value == null || params.value === '') return '';
    const rate = parseFloat(params.value);
    const percentage = rate < 1 ? rate * 100 : rate;
    return `${percentage.toFixed(2)}%`;
  }
},
{ field: 'AverageFXRate', headerName: 'Average FX Rate', minWidth: 140 },
{
  field: 'BeginningSpotRate',
  headerName: 'Begin Spot Rate',
  minWidth: 140,
  valueFormatter: (params) => {
    if (params.value == null || params.value === '') return '';
    return parseFloat(params.value).toFixed(6);
  }
},
{
  field: 'EndingSpotRate',
  headerName: 'End Spot Rate',
  minWidth: 140,
  valueFormatter: (params) => {
    if (params.value == null || params.value === '') return '';
    return parseFloat(params.value).toFixed(6);
  }
}];


const defaultEntityInput = {
  ScenarioCode: '',
  EntityCode: '',
  CurrentTaxRate: '',
  PriorCurrentTaxRate: '',
  BeginningDeferredTaxRate: '',
  EndingDeferredTaxRate: '',
  AverageFXRate: '',
  BeginningSpotRate: '',
  EndingSpotRate: ''
};

const EntityInputs = () => {
  const { selectedScenario } = useScenario();
  const { selectedEntity } = useEntityFilter();

  const [allRows, setAllRows] = useState([]); // Store all entity inputs
  const [rows, setRows] = useState([]); // Filtered rows by scenario
  const [inputs, setInputs] = useState([]); // For export compatibility
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRow, setCurrentRow] = useState(defaultEntityInput);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedRows, setSelectedRows] = useState([]);

  // Import state
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStats, setImportStats] = useState({ total: 0, updated: 0, failed: 0, skipped: 0, notFound: 0 });

  // Master entities for dropdown
  const [masterEntities, setMasterEntities] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [filteredEntities, setFilteredEntities] = useState([]);

  // Fetch master entities
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load master entities', severity: 'error' });
    } finally {
      setEntityLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterEntities();
  }, []);

  // Fetch all entity inputs
  const fetchEntityInputs = async () => {
    setLoading(true);
    try {
      const response = await api.get('entity-models/input');
      setAllRows(response.data || []);
      setInputs(response.data || []); // Keep inputs in sync for export
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load entity inputs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter rows by selected scenario and entity
  useEffect(() => {
    if (!selectedScenario) {
      setRows([]);
      return;
    }

    let filteredRows = (allRows || []).filter((row) => row.ScenarioCode === selectedScenario.scenarioCode);

    // Apply entity filter if specific entities are selected
    const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntities = entityArr.filter((e) => !e.startsWith('[Group] '));
    if (plainEntities.length > 0) {
      filteredRows = filteredRows.filter((row) => plainEntities.includes(row.EntityCode));
    }

    setRows(filteredRows);
  }, [allRows, selectedScenario, selectedEntity]);

  // Filter entities by selected scenario
  useEffect(() => {
    if (!selectedScenario || !masterEntities.length) {
      setFilteredEntities([]);
      return;
    }

    // Parse the selected scenario to get year and period
    const scenarioParts = selectedScenario.scenarioCode.split('-');
    let scenarioYear, scenarioPeriod;

    if (scenarioParts.length >= 2) {
      scenarioYear = parseInt(scenarioParts[0]);
      scenarioPeriod = scenarioParts[1];
    } else {
      // Fallback: use current scenario's year and period if available
      scenarioYear = selectedScenario.year;
      scenarioPeriod = selectedScenario.period;
    }

    const filtered = masterEntities.filter((entity) =>
    entity.Year === scenarioYear && entity.Period === scenarioPeriod
    );

    setFilteredEntities(filtered);
  }, [masterEntities, selectedScenario]);

  useEffect(() => {
    fetchEntityInputs();
  }, []);

  // Dialog handlers
  const handleOpenDialog = (row = defaultEntityInput) => {
    // Prepopulate ScenarioCode for new rows
    if (!row || Object.keys(row).length === 0 || row === defaultEntityInput) {
      setCurrentRow({ ...defaultEntityInput, ScenarioCode: selectedScenario?.scenarioCode || '' });
      setEditMode(false);
    } else {
      setCurrentRow(row);
      setEditMode(!!row && !!row.id);
    }
    setOpenDialog(true);
  };
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRow(defaultEntityInput);
    setEditMode(false);
    setConfirmCloseOpen(false);
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const dataToExport = selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows;

      // Filter data to include only visible columns from ENTITY_INPUT_COLUMNS
      const filteredData = dataToExport.map((row) => {
        const filteredRow = {};
        ENTITY_INPUT_COLUMNS.forEach((col) => {
          filteredRow[col.headerName] = row[col.field];
        });
        return filteredRow;
      });

      // Always include headers even if no data by specifying header option
      const headers = ENTITY_INPUT_COLUMNS.map((col) => col.headerName);
      const ws = XLSX.utils.json_to_sheet(filteredData, {
        header: headers
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'EntityInputs');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      saveAs(blob, 'entity_inputs.xlsx');
      setNotification({ open: true, message: 'Export successful', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: 'Failed to export entity inputs', severity: 'error' });
    }
  };

  // Handle import button click
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportDialogOpen(true);
    }
    // Reset file input so the same file can be selected again
    event.target.value = null;
  };

  /**
   * Process imported data - STRICT VALIDATION:
   * 1. Only EXISTING EntityInput records can be updated
   * 2. ScenarioCode and EntityCode are used ONLY for matching existing records
   * 3. ScenarioCode and EntityCode CANNOT be changed via import
   * 4. New records CANNOT be created via import
   * 5. Only the following rate fields can be updated:
   *    - CurrentTaxRate
   *    - PriorCurrentTaxRate
   *    - BeginningDeferredTaxRate
   *    - EndingDeferredTaxRate
   *    - AverageFXRate
   *    - BeginningSpotRate
   *    - EndingSpotRate
   */
  const importFromExcel = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      setImportProgress(0);
      setImportStats({ total: 0, updated: 0, failed: 0, skipped: 0, notFound: 0 });

      // Read the Excel file
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first worksheet
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            throw new Error('The Excel file is empty');
          }

          // Get all column names from the first row
          const fileColumns = Object.keys(jsonData[0] || {});
          const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

          // Map frontend column names to possible Excel column names
          const columnMappings = {
            'ScenarioCode': ['scenario code', 'scenariocode'],
            'EntityCode': ['entity code', 'entitycode'],
            'CurrentTaxRate': ['current tax rate', 'currenttaxrate'],
            'PriorCurrentTaxRate': ['prior current tax rate', 'priorcurrenttaxrate'],
            'BeginningDeferredTaxRate': ['beginning deferred tax rate', 'begin deferred tax rate', 'beginningdeferredtaxrate'],
            'EndingDeferredTaxRate': ['ending deferred tax rate', 'end deferred tax rate', 'endingdeferredtaxrate'],
            'AverageFXRate': ['average fx rate', 'averagefxrate'],
            'BeginningSpotRate': ['beginning spot rate', 'begin spot rate', 'beginningspotrate'],
            'EndingSpotRate': ['ending spot rate', 'end spot rate', 'endingspotrate']
          };

          // Find matching columns in the Excel file
          const matchedColumns = {};
          let missingColumns = [];

          for (const [field, possibleNames] of Object.entries(columnMappings)) {
            const found = fileColumnsLower.some((col, index) => {
              if (possibleNames.includes(col.toLowerCase())) {
                matchedColumns[field] = fileColumns[index];
                return true;
              }
              return false;
            });

            // ScenarioCode and EntityCode are required for matching
            if (!found && ['ScenarioCode', 'EntityCode'].includes(field)) {
              missingColumns.push(possibleNames[0]);
            }
          }

          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }

          // Initialize local stats tracking with detailed tracking
          const localStats = {
            total: jsonData.length,
            updated: 0,
            failed: 0,
            skipped: 0,
            notFound: 0 // Track records that don't exist
          };
          setImportStats(localStats);

          const errorLog = []; // Track detailed errors

          // Fetch all existing entity inputs to match against
          const allInputsResponse = await api.get('/entity-models/input');
          const allInputs = allInputsResponse.data || [];

          // Create a lookup map for existing inputs
          const inputsMap = new Map();
          allInputs.forEach((input) => {
            const key = `${input.ScenarioCode}|${input.EntityCode}`;
            inputsMap.set(key, input);
          });

          // Process each row
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const rowNumber = i + 2; // Excel row number (accounting for header)

            try {
              // Extract scenario and entity codes
              const scenarioCode = String(row[matchedColumns.ScenarioCode] || '').trim();
              const entityCode = String(row[matchedColumns.EntityCode] || '').trim();

              if (!scenarioCode || !entityCode) {
                errorLog.push(`Row ${rowNumber}: Missing ScenarioCode or EntityCode`);
                localStats.failed++;
                setImportStats({ ...localStats });
                continue;
              }

              // Find matching existing record - REQUIRED for updates
              const key = `${scenarioCode}|${entityCode}`;
              const existingInput = inputsMap.get(key);

              if (!existingInput) {
                // REJECT: No matching record found - cannot create new records via import
                errorLog.push(`Row ${rowNumber}: No existing record for ${scenarioCode}/${entityCode} - import only updates existing records`);
                localStats.notFound++;
                localStats.skipped++;
                setImportStats({ ...localStats });
                setImportProgress(Math.round((i + 1) / jsonData.length * 100));
                continue;
              }

              // Build update object with only rate fields
              const updateData = {};
              let hasChanges = false;

              // Only update rate fields if they exist in the import file
              const rateFields = [
              'CurrentTaxRate',
              'PriorCurrentTaxRate',
              'BeginningDeferredTaxRate',
              'EndingDeferredTaxRate',
              'AverageFXRate',
              'BeginningSpotRate',
              'EndingSpotRate'];


              rateFields.forEach((field) => {
                if (matchedColumns[field] && row[matchedColumns[field]] !== undefined) {
                  const value = row[matchedColumns[field]];
                  // Handle percentage values - remove % sign if present
                  let cleanValue = value;
                  if (typeof value === 'string' && value.includes('%')) {
                    cleanValue = parseFloat(value.replace('%', '')) / 100;
                  } else if (value !== null && value !== '') {
                    cleanValue = parseFloat(value);
                  }

                  if (cleanValue !== existingInput[field]) {
                    updateData[field] = cleanValue;
                    hasChanges = true;
                  }
                }
              });

              // Only update if there are changes
              if (hasChanges) {
                // Create update payload WITHOUT ScenarioCode/EntityCode to prevent changes
                const { ScenarioCode, EntityCode, ...baseData } = existingInput;
                await api.put(`/entity-models/input/${existingInput.id}`, {
                  ...baseData,
                  ...updateData,
                  // Explicitly preserve original codes (防止修改)
                  ScenarioCode: existingInput.ScenarioCode,
                  EntityCode: existingInput.EntityCode,
                  ModifiedOn: new Date().toISOString(),
                  modifiedBy: 'admin'
                });
                localStats.updated++;
              } else {
                localStats.skipped++;
              }

            } catch (rowError) {
              console.error(`Error processing row ${i + 1}:`, rowError);
              localStats.failed++;
            }

            setImportStats({ ...localStats });
            setImportProgress(Math.round((i + 1) / jsonData.length * 100));
          }

          // Show completion message with detailed feedback
          let message = `Import complete: ${localStats.updated} updated, ${localStats.skipped} skipped`;

          if (localStats.notFound > 0) {
            message += `, ${localStats.notFound} not found (cannot create new records via import)`;
          }
          if (localStats.failed > 0) {
            message += `, ${localStats.failed} failed`;
          }

          // Log errors to console for debugging (silent logging for developers)
          if (errorLog.length > 0) {

          }

          const severity = localStats.failed > 0 || localStats.notFound > 0 ? 'warning' : 'success';

          setNotification({
            open: true,
            message: message,
            severity: severity
          });

          // Refresh data
          fetchEntityInputs();

        } catch (error) {
          console.error('Import error:', error);
          setNotification({
            open: true,
            message: error.message || 'Failed to import entity inputs',
            severity: 'error'
          });
        } finally {
          setImporting(false);
          setImportDialogOpen(false);
          setImportFile(null);
        }
      };

      reader.onerror = () => {
        setNotification({
          open: true,
          message: 'Failed to read file',
          severity: 'error'
        });
        setImporting(false);
        setImportDialogOpen(false);
      };

      reader.readAsArrayBuffer(importFile);

    } catch (error) {
      console.error('Import error:', error);
      setNotification({
        open: true,
        message: 'Failed to import entity inputs',
        severity: 'error'
      });
      setImporting(false);
      setImportDialogOpen(false);
    }
  };


  // Bulk delete selected
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected entity inputs?`)) {
      setDeleting(true);
      try {
        await Promise.all(selectedRows.map((id) => api.delete(`/entity-models/input/${id}`)));
        setNotification({ open: true, message: `Successfully deleted ${selectedRows.length} entity inputs`, severity: 'success' });
        fetchEntityInputs();
        setSelectedRows([]);
      } catch (error) {
        setNotification({ open: true, message: 'Failed to delete entity inputs', severity: 'error' });
      } finally {
        setDeleting(false);
      }
    }
  };



  // CRUD actions
  const handleSave = async () => {
    try {
      if (editMode && currentRow.id) {
        const response = await api.put(`/entity-models/input/${currentRow.id}`, currentRow);

        // Check if adjustments were recalculated due to FX rate change
        if (response.data._adjustmentsRecalculated !== undefined) {
          const count = response.data._adjustmentsRecalculated;
          setNotification({
            open: true,
            message: `Entity input updated. ${count} adjustment${count !== 1 ? 's' : ''} recalculated with new FX rate.`,
            severity: 'success'
          });
        } else if (response.data._warning) {
          setNotification({
            open: true,
            message: response.data._warning,
            severity: 'warning'
          });
        } else {
          setNotification({ open: true, message: 'Entity input updated', severity: 'success' });
        }
      } else {
        await api.post('/entity-models/input', {
          ...currentRow,
          dateCreated: new Date().toISOString(),
          ModifiedOn: new Date().toISOString(),
          modifiedBy: 'admin' // TODO: Replace with real user if available
        });
        setNotification({ open: true, message: 'Entity input created', severity: 'success' });
      }
      handleCloseDialog();
      fetchEntityInputs();
    } catch (error) {
      setNotification({ open: true, message: 'Failed to save entity input', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entity-models/input/${id}`);
      setNotification({ open: true, message: 'Entity input deleted', severity: 'success' });
      fetchEntityInputs();
    } catch (error) {
      setNotification({ open: true, message: 'Failed to delete entity input', severity: 'error' });
    }
  };

  // Table columns with edit/delete actions
  const columns = [
  ...ENTITY_INPUT_COLUMNS,
  {
    field: 'actions',
    headerName: 'Actions',
    minWidth: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) =>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)} title="Edit Entity Input">
  <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
      </IconButton>
      <IconButton size="small" onClick={() => handleDelete(params.row.id)} title="Delete Entity Input">
  <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
      </IconButton>
      {/* StateInputs link icon */}
      <IconButton
        size="small"
        component={Link}
        to={`/states-inputs?entity=${encodeURIComponent(params.row.EntityCode)}`}
        title="Go to State Inputs"
        sx={{ color: 'action.active' }}>

  <LaunchIcon sx={{ fontSize: 18 }} />
      </IconButton>
        </Box>

  }];


  return (
    <div className="entity-inputs-container">
      <div className="main-content">
        <div className="toolbar-container">
          <Typography className="page-title" variant="h6">Input Entities</Typography>
          <div className="button-group">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              className="action-button"
              onClick={exportToExcel}
              disabled={inputs.length === 0}>

              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              className="action-button"
              onClick={handleImportClick}>

              Import
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
              style={{ display: 'none' }} />

            <Button
              variant="outlined"
              size="small"
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0 || deleting}
              className="action-button"
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4
                }
              }}>

              {deleting ? 'Deleting...' : 'Remove'}
            </Button>
          </div>
        </div>
        <div className="table-container">
          {loading ? <LinearProgress /> :
          <SearchableDataTable
            searchPlaceholder="Search Entity Inputs..."
            rows={rows}
            columns={columns}
            loading={loading}
            tableName="entityInputs"
            checkboxSelection
            selectionModel={selectedRows}
            onSelectionModelChange={setSelectedRows}
            height="100%"
            className="custom-datagrid" />

          }
        </div>
      </div>
      {/* Dialog for Add/Edit */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleCloseDialog}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog open={openDialog} onClose={handleRequestClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Entity Input' : 'Add Entity Input'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {ENTITY_INPUT_COLUMNS.filter((col) => !['dateCreated', 'ModifiedOn', 'modifiedBy'].includes(col.field)).map((col) => {
              if (col.field === 'EntityCode') {
                return (
                  <Grid item xs={12} sm={6} key="EntityCode">
                     <Autocomplete
                      options={filteredEntities.map((e) => e.EntityCode).sort((a, b) => a.localeCompare(b))}
                      loading={entityLoading}
                      value={currentRow.EntityCode || ''}
                      onChange={(e, newValue) => {
                        setCurrentRow({
                          ...currentRow,
                          EntityCode: newValue || '',
                          // Optionally populate EntityName if available
                          EntityName: filteredEntities.find((ent) => ent.EntityCode === newValue)?.EntityName || ''
                        });
                      }}
                      renderInput={(params) =>
                      <TextField {...params} label={col.headerName} fullWidth required autoComplete="off" />
                      } />

                   </Grid>);

              }
              // Determine field type and hints
              const isFXRate = col.field === 'AverageFXRate' || col.field === 'BeginningSpotRate' || col.field === 'EndingSpotRate';
              const isTaxRate = col.field === 'CurrentTaxRate' || col.field === 'PriorCurrentTaxRate' ||
              col.field === 'BeginningDeferredTaxRate' || col.field === 'EndingDeferredTaxRate';

              let placeholder = '';
              let helperText = '';
              let inputType = 'text';
              let inputProps = {};

              if (isFXRate) {
                inputType = 'number';
                inputProps = { step: '0.000001', min: 0, max: 999999 };
                placeholder = 'xx.xxxxxx';
                helperText = 'Enter direct FX rate (up to 6 decimals)';
              } else if (isTaxRate) {
                inputType = 'number';
                inputProps = { step: '0.01', min: 0, max: 100 };
                placeholder = 'xx.xxxx';
                helperText = 'Enter tax rate as percentage (e.g., 21.00 for 21%)';
              }

              return (
                <Grid item xs={12} sm={6} key={col.field}>
                   <TextField
                    label={col.headerName}
                    fullWidth
                    autoComplete="off"
                    value={currentRow[col.field] || ''}
                    onChange={(e) => setCurrentRow({ ...currentRow, [col.field]: e.target.value })}
                    disabled={col.field === 'id'}
                    type={inputType}
                    inputProps={inputProps}
                    placeholder={placeholder}
                    helperText={helperText} />

                 </Grid>);

            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => !importing && setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Entity Inputs</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Important:</strong> Import only updates rate fields for existing records.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Rows must match existing ScenarioCode and EntityCode combinations<br />
              • Only rate fields can be updated (tax rates, FX rates, spot rates)<br />
              • ScenarioCode and EntityCode cannot be changed<br />
              • New rows cannot be added via import
            </Typography>
            {importFile &&
            <Typography variant="body2" sx={{ mb: 2 }}>
                Selected file: <strong>{importFile.name}</strong>
              </Typography>
            }
            {importing &&
            <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={importProgress} />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  {importProgress}% - Processing {importStats.total} rows...
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Updated: {importStats.updated} | Skipped: {importStats.skipped} | Failed: {importStats.failed}
                </Typography>
              </Box>
            }
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={importFromExcel} variant="contained" disabled={!importFile || importing}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>);

};

export default EntityInputs;
